import {
  Component,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { PageState } from "@/components/ui/PageState";
import { routeRecoveryNavigationState } from "@/lib/navigation/routeRecovery";
import { isRouteResourceError } from "./lazyRoute";

interface BoundaryProps {
  children: ReactNode;
  fallback: (error: Error, retry: () => void) => ReactNode;
}

class RouteBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Route rendering failed", error, info.componentStack);
  }

  private retry = () => this.setState({ error: null });

  render() {
    return this.state.error
      ? this.props.fallback(this.state.error, this.retry)
      : this.props.children;
  }
}

interface RouteRecoveryBoundaryProps {
  children: ReactNode;
  returnTo?: string;
  canReload?: () => boolean;
}

function RouteRecoveryView({
  error,
  retry,
  returnTo = "/workspace",
  canReload = () => true,
}: Omit<RouteRecoveryBoundaryProps, "children"> & {
  error: Error;
  retry: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reloadDenied, setReloadDenied] = useState(false);
  const resourceFailure = isRouteResourceError(error);
  const reloadSafe = resourceFailure && !reloadDenied && canReload();
  const title = t(resourceFailure
    ? "routeRecovery.resource.title"
    : "routeRecovery.business.title");
  const description = t(resourceFailure
    ? reloadSafe
      ? "routeRecovery.resource.reloadDescription"
      : "routeRecovery.resource.returnDescription"
    : "routeRecovery.business.description");

  const reload = () => {
    if (!canReload()) {
      setReloadDenied(true);
      return;
    }
    window.location.reload();
  };

  return (
    <div className="page-container" data-testid="route-recovery">
      <PageState kind="error" title={title} description={description} />
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {reloadSafe ? (
          <Button
            type="button"
            variant="accent"
            data-testid="route-recovery-reload"
            onClick={reload}
          >
            {t("routeRecovery.actions.reload")}
          </Button>
        ) : null}
        {!resourceFailure ? (
          <Button
            type="button"
            variant="outline"
            data-testid="route-recovery-retry"
            onClick={retry}
          >
            {t("actions.retry")}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          data-testid="route-recovery-return"
          onClick={() => navigate(returnTo, {
            state: routeRecoveryNavigationState(returnTo),
          })}
        >
          {t("routeRecovery.actions.return")}
        </Button>
      </div>
    </div>
  );
}

export function RouteRecoveryBoundary({
  children,
  returnTo,
  canReload,
}: RouteRecoveryBoundaryProps) {
  return (
    <RouteBoundary
      fallback={(error, retry) => (
        <RouteRecoveryView
          error={error}
          retry={retry}
          returnTo={returnTo}
          canReload={canReload}
        />
      )}
    >
      {children}
    </RouteBoundary>
  );
}
